import React from "react";
import { Modal, Button, Row, Col, Badge, ProgressBar } from "react-bootstrap";
import { usePromotionalCodeDetails } from "../../hooks/usePromotionalCodeDetails";
import InitialLoader from "components/InitilalLoader";
import {
  getStatusBadgeVariant,
  getDiscountTypeBadgeVariant,
  getCouponTypeBadgeVariant,
  DISCOUNT_TYPE_LABELS,
  COUPON_TYPE_LABELS,
  STATUS_LABELS,
  formatUsageCount,
  CODE_STATUS,
  calculateCodeStatus,
} from "utils/promotionalCode";
import { formatDateTime } from "utils/date";

const PromotionalCodeDetailsModal = ({ show, onHide, codeId }) => {
  const { code, isLoading, error } = usePromotionalCodeDetails(codeId, show && !!codeId);

  if (!show || !codeId) return null;

  const displayCode = code || {};
  const calculatedStatus = calculateCodeStatus(displayCode);
  const usagePercentage =
    displayCode.maxUses && displayCode.maxUses > 0
      ? ((displayCode.usedCount || 0) / displayCode.maxUses) * 100
      : null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Promotional Code Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && <InitialLoader />}
        {error && <div className="text-danger">Error loading code details: {error.message}</div>}
        {!isLoading && !error && displayCode && (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <strong>Code:</strong>
                <p className="mb-0">
                  <code className="fs-5">{displayCode.code}</code>
                </p>
              </Col>
              <Col md={6}>
                <strong>Status:</strong>
                <p className="mb-0">
                  <Badge bg={getStatusBadgeVariant(calculatedStatus)} className="fs-6">
                    {STATUS_LABELS[calculatedStatus] || calculatedStatus}
                  </Badge>
                </p>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <strong>Discount Type:</strong>
                <p className="mb-0">
                  <Badge bg={getDiscountTypeBadgeVariant(displayCode.discountType)}>
                    {DISCOUNT_TYPE_LABELS[displayCode.discountType] || displayCode.discountType}
                  </Badge>
                </p>
              </Col>
              <Col md={6}>
                <strong>Coupon Type:</strong>
                <p className="mb-0">
                  <Badge bg={getCouponTypeBadgeVariant(displayCode.couponType)}>
                    {COUPON_TYPE_LABELS[displayCode.couponType] || displayCode.couponType}
                  </Badge>
                </p>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <strong>Usage:</strong>
                <p className="mb-0">{formatUsageCount(displayCode.usedCount, displayCode.maxUses)}</p>
                {usagePercentage !== null && (
                  <ProgressBar
                    now={usagePercentage}
                    variant={usagePercentage >= 100 ? "danger" : usagePercentage >= 80 ? "warning" : "success"}
                    className="mt-2"
                  />
                )}
              </Col>
              <Col md={6}>
                <strong>Expiry Date:</strong>
                <p className="mb-0">{displayCode.expiresAt ? formatDateTime(displayCode.expiresAt) : "No expiry"}</p>
              </Col>
            </Row>

            {displayCode.description && (
              <Row className="mb-3">
                <Col>
                  <strong>Description:</strong>
                  <p className="mb-0">{displayCode.description}</p>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={6}>
                <strong>Created At:</strong>
                <p className="mb-0">{displayCode.createdAt ? formatDateTime(displayCode.createdAt) : "N/A"}</p>
              </Col>
              <Col md={6}>
                <strong>Updated At:</strong>
                <p className="mb-0">{displayCode.updatedAt ? formatDateTime(displayCode.updatedAt) : "N/A"}</p>
              </Col>
            </Row>

            {/* Show companies that used the code if available */}
            {displayCode.companies && displayCode.companies.length > 0 && (
              <Row className="mb-3">
                <Col>
                  <strong>Used By Companies ({displayCode.companies.length}):</strong>
                  <ul className="mt-2">
                    {displayCode.companies.map((company, index) => (
                      <li key={index}>{company.companyName || company.companyId}</li>
                    ))}
                  </ul>
                </Col>
              </Row>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PromotionalCodeDetailsModal;

